import React, { useState, useEffect } from 'react'
import {
  IonContent,
  IonPage,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonButton,
  IonIcon,
  IonList,
  IonItem,
  IonLabel,
  IonSearchbar,
  IonSegment,
  IonSegmentButton,
  IonChip,
  IonGrid,
  IonRow,
  IonCol,
  IonAlert,
  IonToast,
  IonModal,
  IonButtons,
  IonSelect,
  IonSelectOption,
  IonTextarea,
  IonBadge,
  IonRefresher,
  IonRefresherContent,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonInput
} from '@ionic/react'
import {
  peopleOutline,
  personOutline,
  businessOutline,
  statsChartOutline,
  settingsOutline,
  addOutline,
  searchOutline,
  closeOutline,
  checkmarkCircleOutline,
  warningOutline,
  banOutline,
  mailOutline,
  colorPaletteOutline,
  keyOutline,
  chevronBackOutline,
  chevronForwardOutline,
  playBackOutline,
  playForwardOutline
} from 'ionicons/icons'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '../../store/authStore'
import { supabase } from '../../services/supabase'
import { UserRole } from '../../types'
import ThemeSwitcher from '../../components/ThemeSwitcher'
import PageHeader from '../../components/PageHeader'
import Avatar from '../../components/Avatar'

interface AdminUser {
  id: string
  email: string
  full_name: string
  role: UserRole
  avatar_url?: string
  profile_picture_url?: string
  profile_picture_visibility?: 'public' | 'private' | 'none'
  created_at: string
  updated_at: string
  profile_completed: boolean
  onboarding_completed: boolean
  specializations?: string[]
  last_login?: string
}

interface AdminStats {
  totalUsers: number
  totalCoaches: number
  totalCoachees: number
  activeRelationships: number
  totalSessions: number
  recentRegistrations: number
}

const AdminDashboard: React.FC = () => {
  const { user, isAdmin } = useAuthStore()
  const { t } = useTranslation()

  const [users, setUsers] = useState<AdminUser[]>([])
  const [filteredUsers, setFilteredUsers] = useState<AdminUser[]>([])
  const [stats, setStats] = useState<AdminStats>({
    totalUsers: 0,
    totalCoaches: 0,
    totalCoachees: 0,
    activeRelationships: 0,
    totalSessions: 0,
    recentRegistrations: 0
  })
  const [selectedSegment, setSelectedSegment] = useState<'all' | 'coaches' | 'coachees'>('all')
  const [searchText, setSearchText] = useState('')
  const [loading, setLoading] = useState(true)
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(5) // Changed to 5 to see pagination with fewer users
  const [paginatedUsers, setPaginatedUsers] = useState<AdminUser[]>([])
  const [isThemeSwitcherOpen, setIsThemeSwitcherOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null)
  const [isUserModalOpen, setIsUserModalOpen] = useState(false)
  const [showAlert, setShowAlert] = useState(false)
  const [alertMessage, setAlertMessage] = useState('')
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  
  // User editing state
  const [isEditingUser, setIsEditingUser] = useState(false)
  const [editingUserData, setEditingUserData] = useState<{
    role: UserRole
    profile_completed: boolean
    onboarding_completed: boolean
  } | null>(null)
  
  // Password change state
  const [showPasswordModal, setShowPasswordModal] = useState(false)
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [passwordError, setPasswordError] = useState('')

  // Delete Account Modal State
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [isDeletingAccount, setIsDeletingAccount] = useState(false)
  const [deleteConfirmation, setDeleteConfirmation] = useState('')

  // Email Change Modal State
  const [showEmailModal, setShowEmailModal] = useState(false)
  const [newEmail, setNewEmail] = useState('')
  const [emailError, setEmailError] = useState('')
  const [isChangingEmail, setIsChangingEmail] = useState(false)

  useEffect(() => {
    if (user && isAdmin()) {
      fetchUsers()
      fetchStats()
    }
  }, [user, isAdmin])

  useEffect(() => {
    filterUsers()
  }, [users, selectedSegment, searchText])

  useEffect(() => {
    paginateUsers()
  }, [filteredUsers, currentPage, itemsPerPage])

  const fetchUsers = async () => {
    try {
      setLoading(true)
      const { data, error } = await supabase.rpc('admin_get_all_users')

      if (error) {
        console.error('Admin function error:', error)
        // Fallback to direct query if the function doesn't exist yet
        const { data: fallbackData, error: fallbackError } = await supabase
          .from('profiles')
          .select('*')
          .order('created_at', { ascending: false })
        
        if (fallbackError) {
          console.error('Fallback query error:', fallbackError)
          throw fallbackError
        }
        
        setUsers(fallbackData || [])
        setLoading(false)
        return
      }

      setUsers(data || [])
    } catch (error) {
      console.error('Error fetching users:', error)
      setAlertMessage('Failed to load users')
      setShowAlert(true)
    } finally {
      setLoading(false)
    }
  }

  const fetchStats = async () => {
    try {
      // Try using the comprehensive admin stats function first
      const { data: adminStats, error: adminStatsError } = await supabase
        .rpc('admin_get_dashboard_stats')

      if (adminStats && !adminStatsError) {
        setStats({
          totalUsers: adminStats.totalUsers,
          totalCoaches: adminStats.totalCoaches,
          totalCoachees: adminStats.totalCoachees,
          activeRelationships: adminStats.activeRelationships,
          totalSessions: adminStats.totalSessions,
          recentRegistrations: adminStats.recentRegistrations
        })
        return
      }

      // Fallback to individual queries if the comprehensive function fails
      const { data: allUsers } = await supabase.rpc('admin_get_all_users')
      
      let usersData = allUsers
      
      // Final fallback to direct query if admin function fails
      if (!allUsers) {
        const { data: fallbackUsers } = await supabase
          .from('profiles')
          .select('role, created_at, coach_id')
        usersData = fallbackUsers
      }

      if (usersData) {
        const totalUsers = usersData.length
        const totalCoaches = usersData.filter((u: any) => u.role === 'coach').length
        const totalCoachees = usersData.filter((u: any) => u.role === 'coachee').length
        
        // Recent registrations (last 30 days)
        const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
        const recentRegistrations = usersData.filter((u: any) => 
          new Date(u.created_at) > thirtyDaysAgo
        ).length

        // Try to get active relationships using the specific admin function
        let activeRelationshipsCount = 0
        
        try {
          const { data: activeRels, error: relError } = await supabase
            .rpc('admin_get_active_relationships')
          
          if (activeRels !== null && !relError) {
            activeRelationshipsCount = activeRels
          }
        } catch (error) {
          console.error('Error calling admin_get_active_relationships:', error)
          activeRelationshipsCount = 0
        }

        // Get total sessions
        const { data: sessions } = await supabase
          .from('coaching_sessions')
          .select('id')
        
        setStats({
          totalUsers,
          totalCoaches,
          totalCoachees,
          activeRelationships: activeRelationshipsCount,
          totalSessions: sessions?.length || 0,
          recentRegistrations
        })
      }
    } catch (error) {
      console.error('Error fetching stats:', error)
    }
  }

  const filterUsers = () => {
    let filtered = [...users]

    // Filter by role
    if (selectedSegment !== 'all') {
      const targetRole = selectedSegment === 'coaches' ? 'coach' : 'coachee'
      filtered = filtered.filter(user => user.role === targetRole)
    }

    // Filter by search text
    if (searchText) {
      filtered = filtered.filter(user =>
        user.full_name?.toLowerCase().includes(searchText.toLowerCase()) ||
        user.email.toLowerCase().includes(searchText.toLowerCase())
      )
    }

    setFilteredUsers(filtered)
    // Reset to first page when filters change
    setCurrentPage(1)
  }

  const paginateUsers = () => {
    const startIndex = (currentPage - 1) * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    const paginated = filteredUsers.slice(startIndex, endIndex)
    console.log('Pagination Debug:', {
      totalUsers: filteredUsers.length,
      currentPage,
      itemsPerPage,
      startIndex,
      endIndex,
      paginatedCount: paginated.length
    })
    setPaginatedUsers(paginated)
  }

  const handleUserAction = async (userId: string, action: 'activate' | 'deactivate' | 'delete') => {
    try {
      // Placeholder for user actions
      console.log(`Performing ${action} on user ${userId}`)
      
      if (action === 'delete') {
        const { error } = await supabase
          .from('profiles')
          .delete()
          .eq('id', userId)
        
        if (error) throw error
        
        setToastMessage('User deleted successfully')
        setShowToast(true)
        setIsUserModalOpen(false)
        fetchUsers() // Refresh the list
      }
    } catch (error) {
      console.error(`Error performing ${action}:`, error)
      setAlertMessage(`Failed to ${action} user`)
      setShowAlert(true)
    }
  }

  const updateUserProperties = async (userId: string, updates: {
    role?: UserRole
    profile_completed?: boolean
    onboarding_completed?: boolean
  }) => {
    try {
      console.log('=== UPDATING USER PROPERTIES ===')
      console.log('User ID:', userId)
      console.log('Updates:', updates)
      
      // Use RPC call to bypass RLS issues with admin updates
      const { data, error } = await supabase.rpc('admin_update_user_profile', {
        target_user_id: userId,
        updates: updates
      })
      
      if (error) {
        console.error('RPC call failed, falling back to direct update:', error)
        // Fallback to direct update if RPC doesn't exist
        const { data: fallbackData, error: fallbackError } = await supabase
          .from('profiles')
          .update(updates)
          .eq('id', userId)
          .select()
        
        if (fallbackError) throw fallbackError
        console.log('Database update result (fallback):', fallbackData)
      } else {
        console.log('Database update result (RPC):', data)
      }
      
      // Refresh the users list
      console.log('Refreshing user list...')
      await fetchUsers()
      
      // Update the selected user if it's the same one
      if (selectedUser && selectedUser.id === userId) {
        console.log('Updating selected user state from:', selectedUser)
        const updatedUser = {
          ...selectedUser,
          ...updates
        }
        console.log('Updating selected user state to:', updatedUser)
        setSelectedUser(updatedUser)
      }
      
      setToastMessage('User updated successfully')
      setShowToast(true)
      setIsEditingUser(false)
      setEditingUserData(null)
      
      console.log('=== UPDATE COMPLETE ===')
    } catch (error) {
      console.error('Error updating user:', error)
      setAlertMessage('Failed to update user')
      setShowAlert(true)
    }
  }

  const startEditingUser = (user: AdminUser) => {
    setEditingUserData({
      role: user.role,
      profile_completed: user.profile_completed,
      onboarding_completed: user.onboarding_completed
    })
    setIsEditingUser(true)
  }

  const cancelEditing = () => {
    setIsEditingUser(false)
    setEditingUserData(null)
  }

  const saveUserChanges = () => {
    if (selectedUser && editingUserData) {
      updateUserProperties(selectedUser.id, editingUserData)
    }
  }

  const handlePasswordChange = async () => {
    if (!selectedUser) return
    
    // Reset error
    setPasswordError('')
    
    // Validate passwords
    if (!newPassword || newPassword.length < 6) {
      setPasswordError('Password must be at least 6 characters long')
      return
    }
    
    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match')
      return
    }
    
    try {
      // Use Supabase Admin API to update user password
      const { error } = await supabase.auth.admin.updateUserById(
        selectedUser.id,
        { password: newPassword }
      )
      
      if (error) throw error
      
      setToastMessage('Password updated successfully')
      setShowToast(true)
      setShowPasswordModal(false)
      setNewPassword('')
      setConfirmPassword('')
      setPasswordError('')
    } catch (error) {
      console.error('Error updating password:', error)
      setPasswordError('Failed to update password. Please try again.')
    }
  }

  const openPasswordModal = () => {
    setNewPassword('')
    setConfirmPassword('')
    setPasswordError('')
    setShowPasswordModal(true)
  }

  const handleEmailChange = async () => {
    if (!selectedUser) return
    
    // Reset error
    setEmailError('')
    
    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!newEmail || !emailRegex.test(newEmail)) {
      setEmailError('Please enter a valid email address')
      return
    }
    
    if (newEmail === selectedUser.email) {
      setEmailError('New email must be different from current email')
      return
    }
    
    setIsChangingEmail(true)
    try {
      // Use Supabase Admin API to update user email
      const { error } = await supabase.auth.admin.updateUserById(
        selectedUser.id,
        { email: newEmail }
      )
      
      if (error) throw error
      
      // Also update the email in the profiles table
      const { error: profileError } = await supabase
        .from('profiles')
        .update({ email: newEmail })
        .eq('id', selectedUser.id)
      
      if (profileError) throw profileError
      
      setToastMessage('Email updated successfully')
      setShowToast(true)
      setShowEmailModal(false)
      setNewEmail('')
      setEmailError('')
      
      // Refresh users list
      await fetchUsers()
    } catch (error) {
      console.error('Error updating email:', error)
      setEmailError('Failed to update email. Please try again.')
    } finally {
      setIsChangingEmail(false)
    }
  }

  const openEmailModal = () => {
    setNewEmail('')
    setEmailError('')
    setShowEmailModal(true)
  }

  const getRoleColor = (role: UserRole) => {
    switch (role) {
      case 'coach': return 'primary'
      case 'coachee': return 'secondary'
      case 'admin': return 'tertiary'
      default: return 'medium'
    }
  }

  const getRoleIcon = (role: UserRole) => {
    switch (role) {
      case 'coach': return businessOutline
      case 'coachee': return personOutline
      case 'admin': return settingsOutline
      default: return personOutline
    }
  }

  const doRefresh = async (event: CustomEvent) => {
    await Promise.all([fetchUsers(), fetchStats()])
    event.detail.complete()
  }

  if (!user) {
    return (
      <IonPage>
        <IonContent className="ion-padding">
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
          </div>
        </IonContent>
      </IonPage>
    )
  }

  return (
    <IonPage>
      <PageHeader 
        title="Admin Dashboard"
        showBackButton={false}
      >
        <div className="flex items-center gap-2 mr-2">
          <IonButton 
            fill="clear" 
            size="small"
            onClick={() => setIsThemeSwitcherOpen(true)}
            title="Customize Theme"
          >
            <IonIcon icon={colorPaletteOutline} />
          </IonButton>
        </div>
      </PageHeader>

      <IonContent>
        <IonRefresher slot="fixed" onIonRefresh={doRefresh}>
          <IonRefresherContent></IonRefresherContent>
        </IonRefresher>

        <div className="ion-padding">
          {/* Admin Stats */}
          <IonGrid>
            <IonRow>
              <IonCol size="12" sizeMd="6" sizeLg="3">
                <IonCard>
                  <IonCardContent className="text-center">
                    <IonIcon icon={peopleOutline} className="text-3xl text-primary-500 mb-2" />
                    <div className="text-2xl font-bold text-gray-900">{stats.totalUsers}</div>
                    <div className="text-sm text-gray-600">{t('admin.stats.totalUsers')}</div>
                  </IonCardContent>
                </IonCard>
              </IonCol>
              <IonCol size="12" sizeMd="6" sizeLg="3">
                <IonCard>
                  <IonCardContent className="text-center">
                    <IonIcon icon={businessOutline} className="text-3xl text-primary-500 mb-2" />
                    <div className="text-2xl font-bold text-gray-900">{stats.totalCoaches}</div>
                    <div className="text-sm text-gray-600">{t('admin.stats.coaches')}</div>
                  </IonCardContent>
                </IonCard>
              </IonCol>
              <IonCol size="12" sizeMd="6" sizeLg="3">
                <IonCard>
                  <IonCardContent className="text-center">
                    <IonIcon icon={personOutline} className="text-3xl text-secondary-500 mb-2" />
                    <div className="text-2xl font-bold text-gray-900">{stats.totalCoachees}</div>
                    <div className="text-sm text-gray-600">{t('admin.stats.coachees')}</div>
                  </IonCardContent>
                </IonCard>
              </IonCol>
              <IonCol size="12" sizeMd="6" sizeLg="3">
                <IonCard>
                  <IonCardContent className="text-center">
                    <IonIcon icon={checkmarkCircleOutline} className="text-3xl text-success-500 mb-2" />
                    <div className="text-2xl font-bold text-gray-900">{stats.activeRelationships}</div>
                    <div className="text-sm text-gray-600">{t('admin.stats.activeRelationships')}</div>
                  </IonCardContent>
                </IonCard>
              </IonCol>
            </IonRow>
          </IonGrid>

          {/* Development: Admin Creation - Only show in dev mode and when explicitly needed */}
          {import.meta.env.DEV && !loading && user && !isAdmin() && (
            <IonCard className="border-2 border-warning-500">
              <IonCardHeader>
                <IonCardTitle className="text-warning-600 text-base sm:text-lg">{t('admin.development.createAdmin')}</IonCardTitle>
              </IonCardHeader>
              <IonCardContent>
                <p className="text-sm text-gray-600 mb-4">
                  {t('admin.development.noAdminAccess')}
                </p>
                <p className="text-xs text-orange-600 mb-4">
                  Development Mode Only - Current role: {user.role}
                </p>
                 
                 <IonButton 
                   expand="block" 
                   color="warning"
                   onClick={async () => {
                    if (user?.email) {
                      try {
                        const { data, error } = await supabase
                          .from('profiles')
                          .update({ role: 'admin' })
                          .eq('email', user.email)
                          .select()
                        
                        if (error) throw error
                        
                        setToastMessage(t('admin.development.promotionSuccess'))
                        setShowToast(true)
                        
                        // Refresh the page after a delay
                        setTimeout(() => {
                          window.location.reload()
                        }, 2000)
                      } catch (error) {
                        console.error('Error promoting to admin:', error)
                        setAlertMessage(t('admin.development.promotionError'))
                        setShowAlert(true)
                      }
                    }
                  }}
                >
                  {t('admin.development.promoteToAdmin')}
                </IonButton>
              </IonCardContent>
            </IonCard>
          )}

          {/* User Management */}
          <IonCard>
            <IonCardHeader>
              <IonCardTitle className="flex items-center justify-between text-base sm:text-lg">
                <div className="flex items-center">
                  <IonIcon icon={peopleOutline} className="mr-2" />
                  {t('admin.userManagement')}
                </div>
                <IonBadge color="primary">{filteredUsers.length}</IonBadge>
              </IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              {/* Search and Filter */}
              <div className="mb-4 space-y-4">
                <IonSearchbar
                  value={searchText}
                  onIonInput={e => setSearchText(e.detail.value!)}
                  placeholder={t('admin.userList.searchPlaceholder')}
                  showClearButton="focus"
                />
                
                <IonSegment 
                  value={selectedSegment} 
                  onIonChange={e => setSelectedSegment(e.detail.value as any)}
                >
                  <IonSegmentButton value="all">
                    <IonLabel>{t('admin.userList.allUsers')}</IonLabel>
                  </IonSegmentButton>
                  <IonSegmentButton value="coaches">
                    <IonLabel>{t('admin.userList.coaches')}</IonLabel>
                  </IonSegmentButton>
                  <IonSegmentButton value="coachees">
                    <IonLabel>{t('admin.userList.coachees')}</IonLabel>
                  </IonSegmentButton>
                </IonSegment>
              </div>

              {/* Users List */}
              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto"></div>
                  <p className="text-gray-500 mt-2">{t('admin.userList.loadingUsers')}</p>
                </div>
              ) : paginatedUsers.length > 0 ? (
                <>
                  <IonList className="admin-user-list">
                    {paginatedUsers.map((userData) => (
                    <IonItem 
                      key={userData.id} 
                      button 
                      onClick={() => {
                        setSelectedUser(userData)
                        setIsUserModalOpen(true)
                      }}
                    >
                                            <div className="flex items-center w-full pl-0 pr-4 py-3">
                        <Avatar 
                          user={userData} 
                          currentUserId={user.id}
                          isAdmin={isAdmin()}
                          size="medium"
                          className="ml-4 mr-3 flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <h3 className="font-medium">
                              {userData.full_name || userData.email}
                            </h3>
                            <div className="flex items-center gap-2 ml-2">
                              <IonChip color={getRoleColor(userData.role)} className="text-xs">
                                {userData.role}
                              </IonChip>
                              {!userData.onboarding_completed && (
                                <IonBadge color="warning" className="text-xs">{t('admin.userList.pending')}</IonBadge>
                              )}
                            </div>
                          </div>
                          <p className="text-sm text-gray-600" style={{ 
                            wordBreak: 'break-word',
                            overflowWrap: 'break-word'
                          }}>
                            {userData.email.replace('@', '\u200B@')}
                          </p>
                          <p className="text-xs text-gray-500">
                            {t('admin.userList.joined')}: {new Date(userData.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </IonItem>
                  ))}
                </IonList>
                
                {/* Pagination Controls */}
                {filteredUsers.length > 0 && (
                  <div className="mt-4 space-y-4">
                    {/* Items per page selector */}
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center flex-nowrap">
                        <span className="text-sm text-gray-600 whitespace-nowrap mr-1">{t('admin.userList.pagination.show')}</span>
                        <IonSelect
                          value={itemsPerPage}
                          placeholder="Select"
                          onIonChange={(e) => {
                            setItemsPerPage(e.detail.value)
                            setCurrentPage(1) // Reset to first page
                          }}
                          className="min-w-[50px] max-w-[60px]"
                        >
                          <IonSelectOption value={3}>3</IonSelectOption>
                          <IonSelectOption value={5}>5</IonSelectOption>
                          <IonSelectOption value={10}>10</IonSelectOption>
                          <IonSelectOption value={25}>25</IonSelectOption>
                          <IonSelectOption value={50}>50</IonSelectOption>
                        </IonSelect>
                      </div>
                      
                      <div className="text-sm text-gray-600">
                        {t('admin.userList.pagination.showing')} {((currentPage - 1) * itemsPerPage) + 1}-{Math.min(currentPage * itemsPerPage, filteredUsers.length)} {t('admin.userList.pagination.of')} {filteredUsers.length}
                      </div>
                    </div>
                    
                    {/* Page navigation */}
                    <div className="flex items-center justify-center gap-1 sm:gap-2">
                      <IonButton
                        fill="outline"
                        size="small"
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage(1)}
                        className="sm:min-w-[70px]"
                      >
                        <IonIcon icon={playBackOutline} className="sm:hidden" />
                        <span className="hidden sm:inline">{t('admin.userList.pagination.first')}</span>
                      </IonButton>
                      <IonButton
                        fill="outline"
                        size="small"
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage(currentPage - 1)}
                        className="sm:min-w-[70px]"
                      >
                        <IonIcon icon={chevronBackOutline} className="sm:hidden" />
                        <span className="hidden sm:inline">{t('admin.userList.pagination.previous')}</span>
                      </IonButton>
                      
                      <span className="mx-2 sm:mx-4 text-sm whitespace-nowrap">
                        <span className="hidden sm:inline">{t('admin.userList.pagination.page')} </span>
                        {currentPage}
                        <span className="hidden sm:inline"> {t('admin.userList.pagination.of')} </span>
                        <span className="sm:hidden">/</span>
                        {Math.ceil(filteredUsers.length / itemsPerPage)}
                      </span>
                      
                      <IonButton
                        fill="outline"
                        size="small"
                        disabled={currentPage === Math.ceil(filteredUsers.length / itemsPerPage)}
                        onClick={() => setCurrentPage(currentPage + 1)}
                        className="sm:min-w-[70px]"
                      >
                        <IonIcon icon={chevronForwardOutline} className="sm:hidden" />
                        <span className="hidden sm:inline">{t('admin.userList.pagination.next')}</span>
                      </IonButton>
                      <IonButton
                        fill="outline"
                        size="small"
                        disabled={currentPage === Math.ceil(filteredUsers.length / itemsPerPage)}
                        onClick={() => setCurrentPage(Math.ceil(filteredUsers.length / itemsPerPage))}
                        className="sm:min-w-[70px]"
                      >
                        <IonIcon icon={playForwardOutline} className="sm:hidden" />
                        <span className="hidden sm:inline">{t('admin.userList.pagination.last')}</span>
                      </IonButton>
                    </div>
                  </div>
                )}
              </>
              ) : (
                <div className="text-center py-8">
                  <IonIcon icon={searchOutline} className="text-4xl text-gray-400 mb-4" />
                  <p className="text-gray-500">{t('admin.userList.noUsersFound')}</p>
                </div>
              )}
            </IonCardContent>
          </IonCard>
        </div>

        {/* User Detail Modal */}
        <IonModal isOpen={isUserModalOpen} onDidDismiss={() => {
          setIsUserModalOpen(false)
          setIsEditingUser(false)
          setEditingUserData(null)
        }}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>{t('admin.userDetails.title')}</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setIsUserModalOpen(false)}>
                  <IonIcon icon={closeOutline} />
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            {selectedUser && (
              <div className="space-y-4">
                <IonCard>
                  <IonCardHeader>
                    <IonCardTitle className="flex items-center text-base sm:text-lg">
                      <IonIcon icon={getRoleIcon(selectedUser.role)} className="mr-2 flex-shrink-0" />
                      {selectedUser.full_name || selectedUser.email}
                    </IonCardTitle>
                  </IonCardHeader>
                  <IonCardContent>
                    <div className="space-y-3">
                      <div>
                        <strong>{t('admin.userDetails.email')}:</strong> {selectedUser.email}
                      </div>
                      
                      {/* Editable Role */}
                      <div className="flex items-center justify-between">
                        <strong>{t('admin.userDetails.role')}:</strong>
                        {isEditingUser ? (
                          <IonSelect
                            value={editingUserData?.role}
                            placeholder="Select Role"
                            onIonChange={(e) => 
                              setEditingUserData(prev => prev ? {...prev, role: e.detail.value} : null)
                            }
                            className="ml-2"
                          >
                            <IonSelectOption value="admin">Admin</IonSelectOption>
                            <IonSelectOption value="coach">Coach</IonSelectOption>
                            <IonSelectOption value="coachee">Coachee</IonSelectOption>
                          </IonSelect>
                        ) : (
                          <IonChip 
                            color={getRoleColor(selectedUser.role)} 
                            className="ml-2 cursor-pointer"
                            onClick={() => startEditingUser(selectedUser)}
                          >
                            {selectedUser.role}
                          </IonChip>
                        )}
                      </div>
                      
                      <div>
                        <strong>{t('admin.userList.joined')}:</strong> {new Date(selectedUser.created_at).toLocaleDateString()}
                      </div>
                      
                      {/* Editable Profile Completed */}
                      <div className="flex items-center justify-between">
                        <strong>{t('admin.userDetails.profileCompleted')}:</strong>
                        {isEditingUser ? (
                          <IonSelect
                            value={editingUserData?.profile_completed}
                            placeholder="Select Status"
                            onIonChange={(e) => 
                              setEditingUserData(prev => prev ? {...prev, profile_completed: e.detail.value} : null)
                            }
                            className="ml-2"
                          >
                            <IonSelectOption value={true}>{t('admin.userDetails.yes')}</IonSelectOption>
                            <IonSelectOption value={false}>{t('admin.userDetails.no')}</IonSelectOption>
                          </IonSelect>
                        ) : (
                          <IonChip 
                            color={selectedUser.profile_completed ? 'success' : 'warning'} 
                            className="ml-2 cursor-pointer"
                            onClick={() => startEditingUser(selectedUser)}
                          >
                            {selectedUser.profile_completed ? t('admin.userDetails.yes') : t('admin.userDetails.no')}
                          </IonChip>
                        )}
                      </div>
                      
                      {/* Editable Onboarding Completed */}
                      <div className="flex items-center justify-between">
                        <strong>{t('admin.userDetails.onboardingCompleted')}:</strong>
                        {isEditingUser ? (
                          <IonSelect
                            value={editingUserData?.onboarding_completed}
                            placeholder="Select Status"
                            onIonChange={(e) => 
                              setEditingUserData(prev => prev ? {...prev, onboarding_completed: e.detail.value} : null)
                            }
                            className="ml-2"
                          >
                            <IonSelectOption value={true}>{t('admin.userDetails.yes')}</IonSelectOption>
                            <IonSelectOption value={false}>{t('admin.userDetails.no')}</IonSelectOption>
                          </IonSelect>
                        ) : (
                          <IonChip 
                            color={selectedUser.onboarding_completed ? 'success' : 'warning'} 
                            className="ml-2 cursor-pointer"
                            onClick={() => startEditingUser(selectedUser)}
                          >
                            {selectedUser.onboarding_completed ? t('admin.userDetails.yes') : t('admin.userDetails.no')}
                          </IonChip>
                        )}
                      </div>
                      
                      {selectedUser.specializations && (
                        <div>
                          <strong>{t('admin.userDetails.specializations')}:</strong>
                          <div className="mt-1">
                            {selectedUser.specializations.map((spec, index) => (
                              <IonChip key={index} color="primary" className="mr-1">
                                {spec}
                              </IonChip>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Edit Controls */}
                      {isEditingUser && (
                        <div className="flex gap-2 mt-4">
                          <IonButton 
                            size="small" 
                            color="success" 
                            onClick={saveUserChanges}
                          >
                            {t('admin.userDetails.saveChanges')}
                          </IonButton>
                          <IonButton 
                            size="small" 
                            fill="outline" 
                            color="medium" 
                            onClick={cancelEditing}
                          >
                            {t('admin.userDetails.cancel')}
                          </IonButton>
                        </div>
                      )}
                      
                      {!isEditingUser && (
                        <div className="mt-4">
                          <IonButton 
                            size="small" 
                            fill="outline" 
                            color="primary" 
                            onClick={() => startEditingUser(selectedUser)}
                          >
                            {t('admin.userDetails.editUserProperties')}
                          </IonButton>
                        </div>
                      )}
                    </div>
                  </IonCardContent>
                </IonCard>

                {/* Admin Actions */}
                <IonCard>
                  <IonCardHeader>
                    <IonCardTitle className="text-base sm:text-lg">{t('admin.adminActions.title')}</IonCardTitle>
                  </IonCardHeader>
                  <IonCardContent>
                    <div className="space-y-2">
                      <IonButton 
                        expand="block" 
                        fill="outline" 
                        color="primary"
                        onClick={() => {
                          // TODO: Implement send message
                          setToastMessage(t('admin.adminActions.messageFeatureComingSoon'))
                          setShowToast(true)
                        }}
                      >
                        <IonIcon icon={mailOutline} slot="start" />
                        {t('admin.adminActions.sendMessage')}
                      </IonButton>
                      
                      <IonButton 
                        expand="block" 
                        fill="outline" 
                        color="secondary"
                        onClick={openPasswordModal}
                      >
                        <IonIcon icon={keyOutline} slot="start" />
                        {t('admin.adminActions.changePassword')}
                      </IonButton>
                      
                      <IonButton 
                        expand="block" 
                        fill="outline" 
                        color="tertiary"
                        onClick={openEmailModal}
                      >
                        <IonIcon icon={mailOutline} slot="start" />
                        {t('admin.adminActions.changeEmail')}
                      </IonButton>
                      
                      <IonButton 
                        expand="block" 
                        fill="outline" 
                        color="warning"
                        onClick={() => selectedUser && handleUserAction(selectedUser.id, 'deactivate')}
                      >
                        <IonIcon icon={banOutline} slot="start" />
                        {t('admin.adminActions.deactivateUser')}
                      </IonButton>
                      
                      <IonButton 
                        expand="block" 
                        fill="outline" 
                        color="danger"
                        onClick={() => {
                          // TODO: Add confirmation dialog
                          selectedUser && handleUserAction(selectedUser.id, 'delete')
                        }}
                      >
                        <IonIcon icon={warningOutline} slot="start" />
                        {t('admin.adminActions.deleteUser')}
                      </IonButton>
                    </div>
                  </IonCardContent>
                </IonCard>
              </div>
            )}
          </IonContent>
        </IonModal>

        {/* Password Change Modal */}
        <IonModal isOpen={showPasswordModal} onDidDismiss={() => setShowPasswordModal(false)}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>Change Password</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setShowPasswordModal(false)}>
                  <IonIcon icon={closeOutline} />
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            {selectedUser && (
              <div className="space-y-4">
                <IonCard>
                  <IonCardHeader>
                    <IonCardTitle className="flex items-center text-base sm:text-lg">
                      <IonIcon icon={keyOutline} className="mr-2 flex-shrink-0" />
                      Change Password for {selectedUser.full_name || selectedUser.email}
                    </IonCardTitle>
                  </IonCardHeader>
                  <IonCardContent>
                    <div className="space-y-4">
                      <div>
                        <IonLabel position="stacked">New Password</IonLabel>
                        <IonInput
                          type="password"
                          value={newPassword}
                          onIonInput={(e) => setNewPassword(e.detail.value!)}
                          placeholder="Enter new password (min 6 characters)"
                          className="mt-1"
                        />
                      </div>
                      
                      <div>
                        <IonLabel position="stacked">Confirm Password</IonLabel>
                        <IonInput
                          type="password"
                          value={confirmPassword}
                          onIonInput={(e) => setConfirmPassword(e.detail.value!)}
                          placeholder="Confirm new password"
                          className="mt-1"
                        />
                      </div>
                      
                      {passwordError && (
                        <div className="text-red-500 text-sm mt-2">
                          {passwordError}
                        </div>
                      )}
                      
                      <div className="flex gap-2 mt-6">
                        <IonButton 
                          expand="block" 
                          color="primary"
                          onClick={handlePasswordChange}
                          disabled={!newPassword || !confirmPassword}
                        >
                          Update Password
                        </IonButton>
                        <IonButton 
                          expand="block" 
                          fill="outline" 
                          color="medium"
                          onClick={() => setShowPasswordModal(false)}
                        >
                          Cancel
                        </IonButton>
                      </div>
                    </div>
                  </IonCardContent>
                </IonCard>
                
                <IonCard>
                  <IonCardContent>
                    <div className="text-sm text-gray-600">
                      <p><strong>Security Note:</strong></p>
                      <ul className="list-disc list-inside mt-2 space-y-1">
                        <li>Password must be at least 6 characters long</li>
                        <li>User will be required to log in again with the new password</li>
                        <li>This action cannot be undone</li>
                      </ul>
                    </div>
                  </IonCardContent>
                </IonCard>
              </div>
            )}
          </IonContent>
        </IonModal>

        {/* Email Change Modal */}
        <IonModal isOpen={showEmailModal} onDidDismiss={() => setShowEmailModal(false)}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>Change Email</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setShowEmailModal(false)}>
                  <IonIcon icon={closeOutline} />
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            {selectedUser && (
              <div className="space-y-4">
                <IonCard>
                  <IonCardHeader>
                    <IonCardTitle className="flex items-center text-base sm:text-lg">
                      <IonIcon icon={mailOutline} className="mr-2 flex-shrink-0" />
                      Change Email for {selectedUser.full_name || selectedUser.email}
                    </IonCardTitle>
                  </IonCardHeader>
                  <IonCardContent>
                    <div className="space-y-4">
                      <div>
                        <IonLabel position="stacked">New Email</IonLabel>
                        <IonInput
                          type="email"
                          value={newEmail}
                          onIonInput={(e) => setNewEmail(e.detail.value!)}
                          placeholder="Enter new email"
                          className="mt-1"
                        />
                      </div>
                      
                      {emailError && (
                        <div className="text-red-500 text-sm mt-2">
                          {emailError}
                        </div>
                      )}
                      
                      <div className="flex gap-2 mt-6">
                        <IonButton 
                          expand="block" 
                          color="primary"
                          onClick={handleEmailChange}
                          disabled={!newEmail || isChangingEmail}
                        >
                          Update Email
                        </IonButton>
                        <IonButton 
                          expand="block" 
                          fill="outline" 
                          color="medium"
                          onClick={() => setShowEmailModal(false)}
                        >
                          Cancel
                        </IonButton>
                      </div>
                    </div>
                  </IonCardContent>
                </IonCard>
                
                <IonCard>
                  <IonCardContent>
                    <div className="text-sm text-gray-600">
                      <p><strong>Security Note:</strong></p>
                      <ul className="list-disc list-inside mt-2 space-y-1">
                        <li>New email must be different from current email</li>
                        <li>User will be required to log in again with the new email</li>
                        <li>This action cannot be undone</li>
                      </ul>
                    </div>
                  </IonCardContent>
                </IonCard>
              </div>
            )}
          </IonContent>
        </IonModal>

        {/* Theme Switcher Modal */}
        <ThemeSwitcher 
          isOpen={isThemeSwitcherOpen} 
          onClose={() => setIsThemeSwitcherOpen(false)} 
        />

        {/* Toast for notifications */}
        <IonToast
          isOpen={showToast}
          onDidDismiss={() => setShowToast(false)}
          message={toastMessage}
          duration={3000}
          position="top"
          color="success"
        />

        {/* Alert for errors */}
        <IonAlert
          isOpen={showAlert}
          onDidDismiss={() => setShowAlert(false)}
          header="Error"
          message={alertMessage}
          buttons={['OK']}
        />
      </IonContent>
    </IonPage>
  )
}

export default AdminDashboard 